<template>
    <div>
        <template v-if="!isAdmin">
            <PermissionDenied />
        </template>
        <template v-else>
            <vx-card noShadow noRound>
                <div class="mb-4">
                    <h3 class="mb-2">Budget vs Actual Report</h3>
                    <p class="text-muted mb-4">Select Financial Year to view the budget vs actual report.</p>
                </div>
                
                <!-- Filters -->
                <vs-row>
                    <vs-col vs-xs="12" vs-sm="6" vs-lg="4" class="mb-4 px-2">
                        <SelectForeign
                            :model.sync="selectedYears"
                            label="Financial Year *"
                            placeholder="Select Financial Year..."
                            :multiple="true"
                            :load_initial="true"
                            :label_keys="['fy_year']"
                            :show_id_in_label="false"
                            :endpoint="fyEndpoint"
                            container_class="w-full"
                            input_class=""
                            :use_id_as_value="true"
                            :close_on_select="false"
                        />
                        <small class="text-muted mt-1 block">At least one Financial Year is required</small>
                    </vs-col>

                    <vs-col vs-xs="12" vs-sm="6" vs-lg="4" class="mb-4 px-2">
                        <SelectForeign
                            :model.sync="selectedItemGroups"
                            label="Item Group Code *"
                            placeholder="Select Item Group..."
                            :multiple="true"
                            :load_initial="true"
                            :label_keys="['code', 'name']"
                            :show_id_in_label="false"
                            :endpoint="itemGroupEndpoint"
                            container_class="w-full"
                            input_class=""
                            :use_id_as_value="true"
                            :close_on_select="false"
                            :result_filter="item => item.is_active"
                        />
                        <small class="text-muted mt-1 block">At least one Item Group is required</small>
                    </vs-col>
                    
                    <vs-col vs-xs="12" vs-sm="6" vs-lg="2" class="mb-4 px-2">
                        <!-- Hidden label to match dropdown alignment -->
                        <label class="vs-input--label invisible block mb-3">Search</label>
                        <vs-button @click="loadData" color="primary" type="filled" class="w-full" :disabled="loading || selectedYears.length === 0 || selectedItemGroups.length === 0">
                            <font-awesome-icon :icon="searchIcon" class="mr-2" /> Search
                        </vs-button>
                    </vs-col>
                </vs-row>
            </vx-card>

            <!-- Data Table -->
            <vx-card class="mt-4" noShadow noRound v-if="results.length > 0 || loading">
                <div class="mb-4 flex justify-end items-center">
                    <vx-tooltip text="Export Excel" color="success">
                        <vs-button color="success" type="filled" @click="exportExcel" :disabled="loading || results.length === 0" class="mr-2">
                            <font-awesome-icon :icon="excelIcon" />
                        </vs-button>
                    </vx-tooltip>
                    <vx-tooltip text="Refresh Data" color="primary">
                        <vs-button color="primary" type="filled" @click="loadData" :disabled="loading">
                            <font-awesome-icon :icon="redoIcon" />
                        </vs-button>
                    </vx-tooltip>
                </div>
                
                <vs-table :data="results" slot="no-body" :hoverFlat="true" stripe noDataText="No data available">
                    <vs-tr>
                        <vs-th style="min-width: 150px;">Financial Year</vs-th>
                        <vs-th style="min-width: 150px;">Item Group Code</vs-th>
                        <vs-th style="min-width: 180px;">Yearly cost with GST and escalation ( Life cycle cost)</vs-th>
                        <vs-th style="min-width: 120px;">Planned</vs-th>
                        <vs-th style="min-width: 150px;">Additional Budget</vs-th>
                        <vs-th style="min-width: 120px;">Actual</vs-th>
                    </vs-tr>
                    <template>
                        <template v-for="(item, index) in results">
                            <vs-tr :key="index">
                                <vs-td>{{ item.master_year ? item.master_year.fy_year : '-' }}</vs-td>
                                <vs-td>{{ item.item_group_code || (item.master_item_group ? item.master_item_group.code : '-') }}</vs-td>
                                <vs-td>{{ formatNumber(item.life_cycle_cost) }}</vs-td>
                                <vs-td>{{ formatNumber(item.planned_cost) }}</vs-td>
                                <vs-td>{{ formatNumber(item.additional_budget) }}</vs-td>
                                <vs-td>{{ formatNumber(item.actual_cost) }}</vs-td>
                            </vs-tr>
                        </template>
                        <!-- Totals Row -->
                        <vs-tr v-if="totals" class="total-row">
                            <vs-td><strong>Total</strong></vs-td>
                            <vs-td></vs-td>
                            <vs-td><strong>{{ formatNumber(totals.life_cycle_cost) }}</strong></vs-td>
                            <vs-td><strong>{{ formatNumber(totals.planned_cost) }}</strong></vs-td>
                            <vs-td><strong>{{ formatNumber(totals.additional_budget) }}</strong></vs-td>
                            <vs-td><strong>{{ formatNumber(totals.actual_cost) }}</strong></vs-td>
                        </vs-tr>
                    </template>
                </vs-table>
            </vx-card>
            
            <!-- No Data Message -->
            <vx-card class="mt-4" noShadow noRound v-if="!loading && results.length === 0 && hasSearched">
                <div class="text-center py-8">
                    <p class="text-muted">No data found for the selected financial year(s).</p>
                </div>
            </vx-card>
        </template>
    </div>
</template>

<script>
import { Request } from '../../utils/requests';
import SelectForeign from '../../components/letstream/ls-select-foreign/SelectForeign.vue';
import PermissionDenied from '../pages/NotAuthorized.vue';
import { faRedo, faSearch, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

export default {
    components: {
        SelectForeign,
        PermissionDenied
    },
    data() {
        return {
            results: [],
            totals: null,
            masterYears: [],
            loading: false,
            hasSearched: false,
            selectedYears: [],
            selectedItemGroups: []
        }
    },
    computed: {
        redoIcon: () => faRedo,
        searchIcon: () => faSearch,
        excelIcon: () => faFileExcel,
        isAdmin() {
            return this.$store.state.auth.userRole == 'admin'
        },
        fyEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.master_year}?organisation=${orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_year}?is_active=true`;
        },
        itemGroupEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.master_item_group}?organisation=${orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_item_group}?is_active=true`;
        }
    },
    methods: {
        formatNumber(value) {
            if (value === null || value === undefined || value === '') {
                return '-';
            }
            // Format number with commas (Indian format)
            return Number(value).toLocaleString('en-IN');
        },
        loadData() {
            if (this.selectedYears.length === 0 || this.selectedItemGroups.length === 0) {
                this.$vs.notify({
                    time: 3000,
                    title: 'Error',
                    text: "Please select both Financial Year and Item Group!",
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                });
                return;
            }
            
            this.loading = true;
            this.hasSearched = true;
            this.$vs.loading();
            
            let url = this.$store.state.apiendpoints.report_budget_actual;
            
            // Build parameters
            let params = {
                master_year: this.selectedYears.join(','),
                item_group_code: this.selectedItemGroups.join(',')
            };

            if (this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id) {
                params.organisation = this.$store.state.organisation.organisation.id;
            }
            
            Request(this, 'GET', url, params).then(
                (res) => {
                    let responseData = null;
                    
                    if (res && res.status === true && res.data) {
                        responseData = res.data;
                    } else if (res && res.data && res.data.status === true && res.data.data) {
                        responseData = res.data.data;
                    } else if (res && res.data && Array.isArray(res.data.data)) {
                        responseData = res.data;
                    } else if (res && Array.isArray(res.data)) {
                        responseData = { data: res.data, totals: res.totals, master_years: res.master_years };
                    } else {
                        responseData = res;
                    }
                    
                    if (responseData && responseData.data) {
                        this.masterYears = responseData.master_years || [];
                        this.results = Array.isArray(responseData.data) ? responseData.data : [];
                        this.totals = responseData.totals || null;
                    } else {
                        this.results = [];
                        this.totals = null;
                        this.masterYears = [];
                    }
                    
                    this.loading = false;
                    this.$vs.loading.close();
                },
                (err) => {
                    this.loading = false;
                    this.$vs.loading.close();
                    this.$vs.notify({
                        time: 3000,
                        title: 'Error',
                        text: "An error occurred while loading data. Please try again!",
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger'
                    });
                }
            );
        },
        exportExcel() {
            if (this.results.length === 0) return;

            let xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
   </Borders>
   <Font ss:FontName="Calibri" x:Family="Swiss" ss:Size="10"/>
  </Style>
  <Style ss:ID="Header">
   <Font ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#444444" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="Total">
   <Font ss:Bold="1"/>
   <Interior ss:Color="#F8F9FA" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="Number">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
  </Style>
 </Styles>
 <Worksheet ss:Name="Budget vs Actual">
  <Table>
   <Column ss:Width="120"/>
   <Column ss:Width="120"/>
   <Column ss:Width="180"/>
   <Column ss:Width="100"/>
   <Column ss:Width="120"/>
   <Column ss:Width="100"/>
   <Row ss:Height="25">
    <Cell ss:StyleID="Header"><Data ss:Type="String">Financial Year</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Item Group Code</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Yearly cost with GST and escalation ( Life cycle cost)</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Planned</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Additional Budget</Data></Cell>
    <Cell ss:StyleID="Header"><Data ss:Type="String">Actual</Data></Cell>
   </Row>`;

            this.results.forEach(item => {
                xml += `<Row ss:Height="20">
                    <Cell><Data ss:Type="String">${item.master_year ? item.master_year.fy_year : '-'}</Data></Cell>
                    <Cell><Data ss:Type="String">${item.item_group_code || (item.master_item_group ? item.master_item_group.code : '-')}</Data></Cell>
                    <Cell ss:StyleID="Number"><Data ss:Type="Number">${item.life_cycle_cost || 0}</Data></Cell>
                    <Cell ss:StyleID="Number"><Data ss:Type="Number">${item.planned_cost || 0}</Data></Cell>
                    <Cell ss:StyleID="Number"><Data ss:Type="Number">${item.additional_budget || 0}</Data></Cell>
                    <Cell ss:StyleID="Number"><Data ss:Type="Number">${item.actual_cost || 0}</Data></Cell>
                </Row>`;
            });

            if (this.totals) {
                xml += `<Row ss:Height="25" ss:StyleID="Total">
                    <Cell><Data ss:Type="String">Total</Data></Cell>
                    <Cell></Cell>
                    <Cell ss:StyleID="Number"><Data ss:Type="Number">${this.totals.life_cycle_cost || 0}</Data></Cell>
                    <Cell ss:StyleID="Number"><Data ss:Type="Number">${this.totals.planned_cost || 0}</Data></Cell>
                    <Cell ss:StyleID="Number"><Data ss:Type="Number">${this.totals.additional_budget || 0}</Data></Cell>
                    <Cell ss:StyleID="Number"><Data ss:Type="Number">${this.totals.actual_cost || 0}</Data></Cell>
                </Row>`;
            }

            xml += `  </Table>
 </Worksheet>
</Workbook>`;

            const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `budget_vs_actual_${moment().format('YYYYMMDD_HHmmss')}.xls`;
            a.click();
            window.URL.revokeObjectURL(url);
        }
    }
}
</script>

<style lang="scss" scoped>
.flex {
    display: flex;
}

.items-end {
    align-items: flex-end;
}

.justify-between {
    justify-content: space-between;
}

.items-center {
    align-items: center;
}

.mr-2 {
    margin-right: 0.5rem;
}

.py-8 {
    padding-top: 2rem;
    padding-bottom: 2rem;
}

.text-center {
    text-align: center;
}

.text-primary {
    color: rgba(var(--vs-primary), 1);
}

// Total row styling
.total-row {
    background-color: #f8f9fa !important;
    
    td {
        background-color: #f8f9fa !important;
        border-top: 2px solid #dee2e6 !important;
    }
}
</style>
